/**
 * StrategyOverview (R141) — uniform Overview tab for every strategy on the
 * Strategies page. The LAYOUT is identical across strategies; only the `spec`
 * (rules + colours) differs. Sections, in order:
 *   1. Summary — what the strategy does.
 *   2. Entry conditions — every condition listed descriptively.
 *   3. Exit conditions.
 *   4. Candle colours — a swatch + what condition paints it (plus an optional
 *      Higher-Timeframe background-zone legend).
 *   5. Notes & parameters (ISL / Target / strength, etc.).
 *   6. Getting started — the same 1-2-3 footer for every strategy.
 */
import React from 'react';
import { Box, Card, CardContent, CardHeader, Chip, Typography, Divider } from '@mui/material';
import { useAppTheme } from '../../hooks/useAppTheme';

export interface OvCondition {
  /** Short tag shown as a chip (e.g. "B_C1", "BUY"). */
  tag?: string;
  tagColor?: string;
  label: string;
  desc: React.ReactNode;
}
export interface OvColor {
  color: string;
  label: string;
  when: React.ReactNode;
}
export interface StrategyOverviewSpec {
  title: string;
  summary: React.ReactNode;
  entry: OvCondition[];
  exit: OvCondition[];
  candleColors: OvColor[];
  /** Optional Higher-Timeframe background zones (e.g. RockBand). */
  backgroundColors?: OvColor[];
  /** Marks the chart draws that are NOT trades — OM_Simple's W and C. Listed
   *  separately from entry/exit on purpose: a reader who sees a circle on the
   *  chart needs somewhere to look it up, and putting it under "Entry
   *  conditions" would be exactly the wrong answer. */
  chartOnly?: OvCondition[];
  notes?: React.ReactNode;
}

const Swatch: React.FC<{ color: string }> = ({ color }) => (
  <Box component="span" sx={{
    display: 'inline-block', width: 16, height: 16, borderRadius: '3px',
    bgcolor: color, border: '1px solid rgba(128,128,128,0.4)', mr: 1, flexShrink: 0,
  }} />
);

const ConditionRow: React.FC<{ c: OvCondition }> = ({ c }) => {
  // tagColor is always a palette token (C.bull, C.bear, C.neutral…), so the label on it needs
  // the same onSolid treatment as any other filled chip.
  const { C } = useAppTheme();
  return (
  <Box sx={{ display: 'flex', gap: 1, mb: 1.1, alignItems: 'flex-start' }}>
    {c.tag && (
      <Chip label={c.tag} size="small" sx={{
        fontWeight: 700, fontSize: '0.65rem', height: 20, minWidth: 56,
        bgcolor: c.tagColor || 'action.selected', color: c.tagColor ? C.onSolid : 'text.primary',
      }} />
    )}
    <Box>
      <Typography variant="body2" component="span" sx={{ fontWeight: 700 }}>{c.label}</Typography>
      {' — '}
      <Typography variant="body2" component="span" color="text.secondary">{c.desc}</Typography>
    </Box>
  </Box>
  );
};

const ColorRow: React.FC<{ c: OvColor }> = ({ c }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.85 }}>
    <Box sx={{ pt: 0.2 }}><Swatch color={c.color} /></Box>
    <Box>
      <Typography variant="body2" component="span" sx={{ fontWeight: 700 }}>{c.label}</Typography>
      {' — '}
      <Typography variant="body2" component="span" color="text.secondary">{c.when}</Typography>
    </Box>
  </Box>
);

export const StrategyOverview: React.FC<{ spec: StrategyOverviewSpec }> = ({ spec }) => (
  <Box sx={{ maxWidth: 880 }}>
    <Card sx={{ mb: 2 }}>
      <CardHeader title={<Typography variant="h6" fontWeight={700}>{spec.title}</Typography>} />
      <CardContent sx={{ pt: 0 }}>
        <Typography variant="body2" color="text.secondary">{spec.summary}</Typography>
      </CardContent>
    </Card>

    <Card sx={{ mb: 2 }}>
      <CardHeader title={<Typography variant="subtitle1" fontWeight={700}>Entry conditions</Typography>}
        subheader="Every rule that can open a position." />
      <CardContent sx={{ pt: 1 }}>{spec.entry.map((c, i) => <ConditionRow key={i} c={c} />)}</CardContent>
    </Card>

    <Card sx={{ mb: 2 }}>
      <CardHeader title={<Typography variant="subtitle1" fontWeight={700}>Exit conditions</Typography>}
        subheader="What closes (or flips) an open position." />
      <CardContent sx={{ pt: 1 }}>{spec.exit.map((c, i) => <ConditionRow key={i} c={c} />)}</CardContent>
    </Card>

      {!!spec.chartOnly?.length && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardHeader title={<Typography variant="subtitle1" fontWeight={700}>Chart marks (not trades)</Typography>}
            subheader={<Typography variant="caption" color="text.secondary">
              These appear on the chart only. They raise no alert, write no scan row, and do not change
              what the strategy signals.
            </Typography>}
            sx={{ pb: 0 }} />
          <CardContent sx={{ pt: 1 }}>{spec.chartOnly.map((c, i) => <ConditionRow key={i} c={c} />)}</CardContent>
        </Card>
      )}

    <Card sx={{ mb: 2 }}>
      <CardHeader title={<Typography variant="subtitle1" fontWeight={700}>Candle colours</Typography>}
        subheader="What each colour painted on the candles means for this strategy." />
      <CardContent sx={{ pt: 1 }}>
        {spec.candleColors.map((c, i) => <ColorRow key={i} c={c} />)}
        {spec.backgroundColors && spec.backgroundColors.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 1 }}>
              Background zone — Higher-Timeframe
            </Typography>
            {spec.backgroundColors.map((c, i) => <ColorRow key={i} c={c} />)}
          </>
        )}
      </CardContent>
    </Card>

    {spec.notes && (
      <Card sx={{ mb: 2 }}>
        <CardHeader title={<Typography variant="subtitle1" fontWeight={700}>Notes &amp; parameters</Typography>} />
        <CardContent sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">{spec.notes}</Typography>
        </CardContent>
      </Card>
    )}

    <Card>
      <CardHeader title={<Typography variant="subtitle1" fontWeight={700}>Getting started</Typography>} />
      <CardContent sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          1) Set your Interval → Higher-Timeframe mappings in the Timeframe Mapping panel above.
          2) Create a Watchlist and add symbols (Watchlists tab).
          3) Run an Adhoc scan, or configure a Scheduled Scan, from the Strategy Scan tab.
          Confirmed signals appear in the Signal Log tab; scheduled scans can also fan out to
          the Dashboard and/or Telegram per the routing toggles.
        </Typography>
      </CardContent>
    </Card>
  </Box>
);

export default StrategyOverview;
